import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import TextField from '@mui/material/TextField';
import StbButton from '../security-toolbox-ui/StbButton';
import StbModal from '../security-toolbox-ui/StbModal';
import { useLabDataCenters, useSaveLabRow } from '../../hooks/lab-hooks';
import { RootState } from '../../support/store';
import {
  Lab,
  LabOption,
  labDataCenterCode,
  labSlugify,
  LAB_TABLES,
} from '../../support/labService';
import LabDcloudLookupPanel, { LabDcloudPick } from './LabDcloudLookupPanel';
import { LAB_JSON_SCHEMAS } from '../../support/labSpecSchema';
import {
  LabCheckbox,
  LabOptionSelect,
  LabStatusTag,
  labErrorMessage,
} from './LabShared';
import { LabSpecBuilder } from './LabSpecFields';

export type LabEditorModalProps = {
  open: boolean;
  lab: Lab | null;
  /**
   * Every lab already created, so a duplicate slug is caught here rather than as a 400. Only the
   * slug matters: two labs may share a name, and nothing downstream refers to a lab by one.
   */
  existing?: Lab[];
  onClose: () => void;
  onSaved?: (lab: Lab) => void;
};

const EMPTY: Partial<Lab> = {
  name: '',
  slug: '',
  description: '',
  demo_uid: null,
  demo_name: '',
  demo_data_center: null,
  // Filled in from the signed-in user when the form opens on a new lab - see the open effect. Left
  // blank here so this stays a constant rather than something that reads the session at import.
  owner_email: '',
  enabled: true,
  metadata: { customer: 'PseudoCo' },
};

/**
 * Create or edit a lab: the top-level authored unit.
 *
 * Short on purpose, because a lab is a composition rather than a thing. What it *does* is decided in
 * two places that are not this form: the sections it is made of, attached in the builder, and the
 * data centres it is published in, each with its own dCloud template and demo id. Neither belongs
 * here - a section list is a tree, and a template is only meaningful inside one data centre, so a
 * single "dCloud template" field on the lab would be wrong in every data centre but one.
 *
 * The **dCloud demo** is on this form, which sounds like the same objection and is not. A demo id is
 * per data centre too - hence the data centre beside it, which is part of the link rather than a
 * detail next to it - but "which dCloud demo is this lab" is a question about the lab, and it is the
 * one question a publication cannot answer: a lab published in RTP and London has two uids and
 * neither of them is *the* demo. So the lab records the demo it was authored against, and each
 * publication records the id a session in its own data centre will actually report.
 *
 * No course. The narrative belongs to the section it is reading for, so `LabScenario` carries the
 * course link and a lab of three sections can carry three pieces of reading. A lab-level course was
 * a second place to say the same thing, and two places disagree.
 */
const LabEditorModal: React.FC<LabEditorModalProps> = ({
  open,
  lab,
  existing = [],
  onClose,
  onSaved,
}) => {
  const [draft, setDraft] = useState<Partial<Lab>>(EMPTY);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  /** The hand-entry escape hatch, for a data centre whose catalog cannot be read. */
  const [manualDemo, setManualDemo] = useState(false);

  const save = useSaveLabRow<Partial<Lab> & { id?: number }>(LAB_TABLES.lab);

  const { data: dataCenters } = useLabDataCenters();

  /**
   * Who is signed in, so a new lab is owned by whoever authored it.
   *
   * The same email the rest of the app records as the actor, so an owner recorded here matches the
   * one on that person's other rows. Read at open rather than baked into `EMPTY` because it is null
   * until sign-in resolves; this form is only reachable well after that, so the default lands.
   */
  const { email: signedInEmail } = useSelector((state: RootState) => state.user.userData);

  useEffect(() => {
    if (!open) return;

    // A new lab is owned by whoever is authoring it, which is right nearly every time and is
    // editable when it is not. An existing lab keeps the owner it was saved with - including no
    // owner at all: filling that in on open would hand somebody else's lab to whoever opened it,
    // and the save would look like a deliberate reassignment.
    setDraft(lab ? { ...lab } : { ...EMPTY, owner_email: signedInEmail || '' });
    setJsonError(null);
    setFormError(null);
    setManualDemo(false);
    save.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lab?.id]);

  const set = (patch: Partial<Lab>) => setDraft((current) => ({ ...current, ...patch }));

  const demoDataCenter = useMemo(
    () => (dataCenters || []).find((entry) => entry.id === draft.demo_data_center) || null,
    [dataCenters, draft.demo_data_center],
  );

  const dataCenterOptions: LabOption<string>[] = useMemo(
    () =>
      (dataCenters || []).map((entry) => ({
        value: String(entry.id),
        label: entry.dcloud_dc_name,
        hint: entry.dcloud_dc_region || undefined,
      })),
    [dataCenters],
  );

  const hasDemo = draft.demo_uid !== null && draft.demo_uid !== undefined;

  /**
   * Changing the data centre drops the demo with it.
   *
   * Not tidiness: the uid was read from one data centre's catalog and means something else, or
   * nothing, in the next. Keeping it would leave a lab claiming a demo that is not there, which is
   * the failure that only shows up when a session comes up and matches nothing.
   */
  const setDemoDataCenter = (value: string) => {
    set({
      demo_data_center: value ? Number(value) : null,
      ...(hasDemo && Number(value) !== draft.demo_data_center
        ? { demo_uid: null, demo_name: '' }
        : {}),
    });
    setFormError(null);
  };

  /**
   * A pick from the lookup, narrowed to the two columns a lab has.
   *
   * The lookup can also hand over a topology and a template, and neither belongs here: the pod a
   * lab is built on is per data centre and is the publication's business. Taken explicitly rather
   * than spread, so a new column on the pick cannot arrive on this row by accident.
   */
  const applyDemoPick = (pick: LabDcloudPick) => {
    set({ demo_uid: pick.demo_uid ?? null, demo_name: pick.demo_name || '' });
    setFormError(null);
  };

  /**
   * Derived from the name, and only for a new lab.
   *
   * Publications are named after it and events point at those, so an existing lab keeps whatever
   * slug it was saved with: regenerating it on a rename would leave the publications carrying a
   * slug their lab no longer has.
   */
  const generatedSlug = useMemo(
    () => (lab ? lab.slug : labSlugify(draft.name || '')),
    [lab, draft.name],
  );

  const handleSubmit = async () => {
    if (!draft.name?.trim()) {
      setFormError('A lab needs a name. It is what a learner is given.');
      return;
    }

    if (!generatedSlug) {
      setFormError(
        'No slug could be derived from that name, and publications are named from it. Use letters or numbers somewhere in the name.',
      );
      return;
    }

    const clash = existing.find(
      (candidate) => candidate.id !== draft.id && candidate.slug === generatedSlug,
    );

    if (clash) {
      setFormError(
        `"${clash.name}" already has the slug ${generatedSlug}. Change the name enough that the two differ - the slug is what publications are derived from.`,
      );
      return;
    }

    // A uid on its own names nothing: dCloud numbers content per data centre, so demo 12345 is a
    // different demo in RTP than in London and an id with no data centre beside it cannot be
    // checked against any catalog. Refused rather than saved as half a link.
    if (hasDemo && !draft.demo_data_center) {
      setFormError(
        'That demo id has no data centre against it. Pick the data centre it was read in - the same id means a different demo in each.',
      );
      return;
    }

    if (jsonError) {
      setFormError(`Metadata: ${jsonError}`);
      return;
    }

    // Every lab in the catalogue is set at PseudoCo, and the definition loader refuses any other
    // customer. Saying so here beats a 400 from the loader weeks later.
    const customer = String((draft.metadata as any)?.customer || '');

    if (customer && customer !== 'PseudoCo') {
      setFormError(
        `Labs are set at PseudoCo; metadata.customer says "${customer}". The loader will refuse this.`,
      );
      return;
    }

    setFormError(null);

    try {
      const saved = await save.mutateAsync({ ...draft, slug: generatedSlug });
      onSaved?.(saved as Lab);
      onClose();
    } catch {
      // Surfaced through save.error.
    }
  };

  const submitError = formError || (save.isError ? labErrorMessage(save.error) : null);

  return (
    <StbModal
      open={open}
      role="update"
      title={lab ? `Edit ${lab.name}` : 'New lab'}
      size="large"
      scrollable
      submitLabel={lab ? 'Save lab' : 'Create lab'}
      suppressCloseOnSubmit
      onSubmit={handleSubmit}
      onClose={async () => onClose()}
      apiResponseSection={
        submitError ? <p className="m-0 text-sm text-amber-800">{submitError}</p> : undefined
      }
    >
      <div className="flex flex-col gap-4">
        <TextField
          label="Name"
          value={draft.name || ''}
          onChange={(evt) => set({ name: evt.target.value })}
          helperText="What this lab is called wherever it is scheduled."
          fullWidth
        />

        <TextField
          label="Description"
          value={draft.description || ''}
          onChange={(evt) => set({ description: evt.target.value })}
          helperText="One line for the admin lists. The learner reads the course on each section, not this."
          multiline
          minRows={2}
          fullWidth
        />

        {/* The one thing on this form that cannot be typed from memory, and the only field here
            whose meaning depends on a second field: a demo id belongs to one data centre. */}
        <div className="flex flex-col gap-3 rounded-md border border-gray-200 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-semibold text-gray-700">dCloud demo</span>
            {hasDemo ? (
              <LabStatusTag label={`demo ${draft.demo_uid}`} sentiment="success" />
            ) : (
              <LabStatusTag label="Not linked" sentiment="warning" />
            )}
          </div>

          <LabOptionSelect
            label="Data centre the demo lives in"
            value={draft.demo_data_center ? String(draft.demo_data_center) : ''}
            options={dataCenterOptions}
            allowEmpty
            emptyLabel="Pick a data centre"
            onChange={setDemoDataCenter}
            helpText="Which catalog the demo id is read from. Changing it clears the demo, because the same id is a different demo in each data centre."
          />

          {hasDemo ? (
            <div className="flex flex-wrap items-end justify-between gap-2">
              <dl className="m-0 grid grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
                <div>
                  <dt className="inline text-gray-500">Demo id: </dt>
                  <dd className="m-0 inline font-mono">{draft.demo_uid}</dd>
                </div>
                <div>
                  <dt className="inline text-gray-500">Demo name: </dt>
                  <dd className="m-0 inline">{draft.demo_name || '-'}</dd>
                </div>
              </dl>
              <StbButton
                role="default"
                size="xsmall"
                onClick={async () => set({ demo_uid: null, demo_name: '' })}
              >
                Clear demo
              </StbButton>
            </div>
          ) : (
            <p className="m-0 text-xs text-gray-600">
              No demo linked, so nothing records which dCloud environment this lab is built from.
              The lab still runs: what a session is matched on is the demo id on each publication,
              and that is set per data centre when the lab is published.
            </p>
          )}

          {/* Only a real gap gets a warning. A lab whose demo data centre was removed since is
              holding a uid nothing can check, and re-picking it is the only fix. */}
          {hasDemo && !!draft.demo_data_center && !demoDataCenter && (
            <p className="m-0 text-xs text-amber-800">
              {`The data centre this demo was read in (id ${draft.demo_data_center}) is no longer recorded, so the id cannot be checked against any catalog. Pick a data centre and choose the demo again.`}
            </p>
          )}

          {!!draft.demo_data_center && !!demoDataCenter && !labDataCenterCode(demoDataCenter) && (
            <p className="m-0 text-xs text-amber-800">
              {`No dCloud code could be derived from "${demoDataCenter.dcloud_dc_name}", so its catalog cannot be listed. Enter the demo id by hand below.`}
            </p>
          )}

          {/* Inline rather than behind a step: identifying the demo is part of filling this form,
              and the panel is read-only - nothing here writes to dCloud. Templates are hidden
              because a lab has nowhere to put one; that is the publication's field. */}
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
            <LabDcloudLookupPanel
              dataCenterId={draft.demo_data_center ?? null}
              onPick={applyDemoPick}
              showTemplates={false}
            />
          </div>

          <div>
            <StbButton role="default" size="small" onClick={async () => setManualDemo(!manualDemo)}>
              {manualDemo ? 'Hide manual entry' : 'Enter the demo id by hand'}
            </StbButton>
            <p className="m-0 mt-1 text-xs text-gray-500">
              For a data centre whose catalog cannot be read. A pick above fills both fields in.
            </p>
          </div>

          {manualDemo && (
            <div className="flex flex-col gap-3 sm:flex-row">
              <TextField
                label="dCloud demo id"
                type="number"
                value={draft.demo_uid ?? ''}
                onChange={(evt) =>
                  set({ demo_uid: evt.target.value === '' ? null : Number(evt.target.value) })
                }
                helperText="What a session launched from this demo reports as its parent."
                fullWidth
              />
              <TextField
                label="Demo name"
                value={draft.demo_name || ''}
                onChange={(evt) => set({ demo_name: evt.target.value })}
                helperText="A snapshot for reading. The id is what is authoritative."
                fullWidth
              />
            </div>
          )}
        </div>

        <TextField
          label="Owner"
          value={draft.owner_email || ''}
          onChange={(evt) => set({ owner_email: evt.target.value })}
          helperText={
            lab
              ? 'Who to ask about this lab. Recorded as an email; nothing is sent to it from here.'
              : 'Who to ask about this lab. Defaults to you; change it if you are authoring it for somebody else. Nothing is sent to it from here.'
          }
          fullWidth
        />

        <LabCheckbox
          label="Enabled"
          checked={!!draft.enabled}
          onChange={(enabled) => set({ enabled })}
          helpText="A disabled lab is not matched against sessions in any data centre. Its sections stay attached and keep working in whatever other labs use them."
        />

        <LabSpecBuilder
          schema={LAB_JSON_SCHEMAS.labMetadata}
          value={draft.metadata}
          resetKey={lab?.id ?? 'new'}
          onChange={(metadata, error) => {
            setJsonError(error);
            if (!error) set({ metadata });
          }}
        />
      </div>
    </StbModal>
  );
};

export default LabEditorModal;
